import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import type {
  SalesTaxReportResponse,
  SalesTaxReportRow,
  TaxCode,
} from '@repo/api-contract';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridPagination,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import client from '../../util/api-client';
import { Routes } from '../../util/constants';
import { pushToast, useDispatch } from '../../state';
import { serverError } from '../../components/modals/constants/errors';
import { openSearchSalesTaxModal } from '../../state/salesTaxSearchModalSlice';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  usePersistedDataGridColumnPreferences,
} from '../../util/hooks';
import type { DataGridTableId } from '../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../components/dataGrid/DataGridTranslatedColumnHeader';
import hexToRGBA from '../../util/converters/hexToRGBA';

const TABLE_ID = 'sales-tax-report' satisfies DataGridTableId;
const KEY_REPORTS_PAGE = 'team.Components.ReportsPage';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function dateInputToUnix(value: string, endOfDay = false): number | undefined {
  if (!value) return undefined;
  const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
  const date = new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
  return Math.floor(date.getTime() / 1000);
}

function formatCurrency(value: number) {
  return currencyFormatter.format(value);
}

function toDateInput(date: Date) {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function lastThirtyDaysRange() {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  return { from: toDateInput(from), to: toDateInput(to) };
}

function quarterRange(quartersAgo: number) {
  const now = new Date();
  const index = Math.floor(now.getMonth() / 3) - quartersAgo;
  const year = now.getFullYear() + Math.floor(index / 4);
  const quarter = ((index % 4) + 4) % 4;
  return {
    from: toDateInput(new Date(year, quarter * 3, 1)),
    // Day 0 of the following month resolves to the last day of the quarter.
    to: toDateInput(new Date(year, quarter * 3 + 3, 0)),
  };
}

const DATE_PRESETS = [
  { value: 'last30', label: 'Last 30 Days', getRange: lastThirtyDaysRange },
  {
    value: 'currentQuarter',
    label: 'Current Quarter',
    getRange: () => quarterRange(0),
  },
  {
    value: 'previousQuarter',
    label: 'Previous Quarter',
    getRange: () => quarterRange(1),
  },
  { value: 'custom', label: 'Custom', getRange: null },
] as const;

type DatePreset = (typeof DATE_PRESETS)[number]['value'];

function ReportPagination({
  totalLabel,
  ...props
}: ComponentProps<typeof GridPagination> & { totalLabel?: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        gap: 2,
        pr: 1,
      }}
    >
      {totalLabel ? (
        <Typography fontSize={14} fontWeight={600}>
          {totalLabel}
        </Typography>
      ) : null}
      <GridPagination {...props} />
    </Box>
  );
}

export default function SalesTaxReport() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { t } = useTranslation([], {
    keyPrefix: KEY_REPORTS_PAGE,
  });
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [selectedTaxCode, setSelectedTaxCode] = useState<TaxCode | null>(null);
  const initialRange = useMemo(() => lastThirtyDaysRange(), []);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [datePreset, setDatePreset] = useState<DatePreset>('last30');
  const [report, setReport] = useState<SalesTaxReportResponse | null>(null);
  const [loadingTaxCodes, setLoadingTaxCodes] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const reportInput = useMemo(
    () => ({
      dateFrom: dateInputToUnix(dateFrom),
      dateTo: dateInputToUnix(dateTo, true),
      taxCodeId: selectedTaxCode?.TaxCodeId,
    }),
    [dateFrom, dateTo, selectedTaxCode],
  );

  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    },
    [dispatch],
  );

  const loadReport = useCallback(
    async (input: {
      dateFrom?: number;
      dateTo?: number;
      taxCodeId?: string;
    }) => {
      if (
        input.dateFrom != null &&
        input.dateTo != null &&
        input.dateFrom > input.dateTo
      ) {
        dispatch(
          pushToast({ message: t('From must be before To'), type: 'error' }),
        );
        return;
      }

      setLoadingReport(true);
      try {
        const response = await client.getSalesTaxReport.query(input);
        setReport(response);
        setPaginationModel((current) => ({ ...current, page: 0 }));
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingReport(false);
      }
    },
    [dispatch, handleError, t],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadTaxCodes() {
      setLoadingTaxCodes(true);
      try {
        const response = await client.allTaxCodes.query();
        if (!cancelled) {
          setTaxCodes(response.results);
        }
      } catch (error) {
        if (cancelled) return;
        handleError(error);
      } finally {
        if (!cancelled) {
          setLoadingTaxCodes(false);
        }
      }
    }

    loadTaxCodes();

    return () => {
      cancelled = true;
    };
  }, [handleError]);

  useEffect(() => {
    void loadReport({
      dateFrom: dateInputToUnix(initialRange.from),
      dateTo: dateInputToUnix(initialRange.to, true),
    });
    // Run the report once on page open using the default date range.
  }, []);

  const rows = useMemo((): SalesTaxReportRow[] => report?.rows ?? [], [report]);

  const filteredTotal = useMemo(
    () =>
      report?.totalCalculatedAmount ??
      rows.reduce((sum, row) => sum + row.calculatedAmount, 0),
    [report, rows],
  );

  const handleGenerate = () => {
    loadReport(reportInput);
  };

  const handlePresetChange = (value: DatePreset) => {
    setDatePreset(value);
    const preset = DATE_PRESETS.find((option) => option.value === value);
    if (!preset?.getRange) return;
    const range = preset.getRange();
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const handleClearFilters = () => {
    setSelectedTaxCode(null);
    const range = lastThirtyDaysRange();
    setDateFrom(range.from);
    setDateTo(range.to);
    setDatePreset('last30');
  };

  const handleDownload = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const response = await client.downloadSalesTaxReport.query(reportInput);
      window.open(response.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      handleError(error);
    } finally {
      setDownloading(false);
    }
  };

  const baseColumns = useMemo(
    (): GridColDef[] => [
      {
        field: 'taxCodeName',
        headerName: 'Tax Code',
        flex: 1,
        minWidth: 180,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Tax Code"
            keyPrefix={KEY_REPORTS_PAGE}
            params={params}
          />
        ),
      },
      {
        field: 'calculatedAmount',
        headerName: 'Calculated Amount',
        flex: 1,
        minWidth: 180,
        type: 'number',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Calculated Amount"
            keyPrefix={KEY_REPORTS_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SalesTaxReportRow, number>,
        ) => <span>{formatCurrency(params.value ?? 0)}</span>,
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: EMPTY_LEADING_GRID_COLUMNS,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: '#fff',
        p: { xs: 2, md: 4 },
        overflow: 'auto',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          minHeight: 640,
          maxWidth: 1160,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
            {t('Sales Tax Report')}
          </Typography>
        </Box>
        <Divider />

        <Box
          sx={{
            p: { xs: 2, md: 5 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr auto' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Box>
              <Autocomplete
                size="small"
                loading={loadingTaxCodes}
                options={taxCodes}
                value={selectedTaxCode}
                getOptionLabel={(option) => option.codeName}
                isOptionEqualToValue={(option, value) =>
                  option.TaxCodeId === value.TaxCodeId
                }
                onChange={(_, value) => setSelectedTaxCode(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('Search for Tax Code...')}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon color="action" />,
                      endAdornment: (
                        <>
                          {loadingTaxCodes ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Link
                component="button"
                underline="hover"
                sx={{ mt: 1, ml: 1, fontSize: 14 }}
                onClick={() =>
                  dispatch(
                    openSearchSalesTaxModal({
                      multiSelect: false,
                      onImport: (selectedTaxCodes) => {
                        const selected = selectedTaxCodes[0];
                        if (selected) {
                          setSelectedTaxCode(selected);
                        }
                      },
                    }),
                  )
                }
              >
                {t('Browse Tax Code')}
              </Link>
            </Box>

            <Stack spacing={1}>
              <TextField
                select
                size="small"
                label={t('Date Range')}
                value={datePreset}
                onChange={(event) =>
                  handlePresetChange(event.target.value as DatePreset)
                }
              >
                {DATE_PRESETS.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {t(preset.label)}
                  </MenuItem>
                ))}
              </TextField>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={t('From')}
                    value={dateFrom ? dayjs(dateFrom) : null}
                    onChange={(value: Dayjs | null) => {
                      setDateFrom(
                        value?.isValid() ? value.format('YYYY-MM-DD') : '',
                      );
                      setDatePreset('custom');
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label={t('To')}
                    value={dateTo ? dayjs(dateTo) : null}
                    onChange={(value: Dayjs | null) => {
                      setDateTo(
                        value?.isValid() ? value.format('YYYY-MM-DD') : '',
                      );
                      setDatePreset('custom');
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </LocalizationProvider>
              </Stack>
            </Stack>

            <Stack alignItems={{ xs: 'stretch', md: 'flex-end' }} spacing={3}>
              <Link
                component="button"
                underline="hover"
                onClick={handleClearFilters}
                sx={{ fontSize: 14 }}
              >
                {t('Clear Filters')}
              </Link>
              <Button
                variant="contained"
                size="small"
                disabled={loadingReport}
                onClick={handleGenerate}
              >
                {loadingReport ? t('Generating') : t('Generate')}
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={!report || downloading}
              onClick={handleDownload}
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadOutlinedIcon />
                )
              }
            >
              {downloading ? t('Downloading') : t('Download')}
            </Button>
          </Box>

          <Box sx={{ mt: 2, height: 480, width: '100%' }}>
            <DataGridPro
              apiRef={apiRef}
              columnHeaderHeight={56}
              sx={{
                border: 'none',
                '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
                  backgroundColor: '#ECECEC',
                },
                '.MuiDataGrid-row.Mui-selected': {
                  backgroundColor: (theme) =>
                    hexToRGBA(theme.palette.primary.main, 0.19),
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
                '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
              }}
              rows={rows.map((row) => ({
                ...row,
                id: row.taxCodeId,
              }))}
              loading={loadingReport}
              columns={columns}
              columnVisibilityModel={persistedPrefs.columnVisibilityModel}
              onColumnVisibilityModelChange={
                persistedPrefs.onColumnVisibilityModelChange
              }
              onColumnWidthChange={persistedPrefs.onColumnWidthChange}
              onColumnOrderChange={persistedPrefs.onColumnOrderChange}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
              disableRowSelectionOnClick
              disableColumnFilter
              autoHeight={false}
              slots={{
                pagination: ReportPagination,
              }}
              slotProps={{
                pagination: {
                  totalLabel: `${t('Total')}: ${formatCurrency(filteredTotal)}`,
                } as never,
              }}
              localeText={{
                noRowsLabel: report
                  ? t('No sales tax rows found')
                  : t('Generate the report to view sales tax totals'),
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
